import * as React from 'react';
import { Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BellRing } from 'lucide-react';
import { useT } from '../lib/i18n.js';
import {
  listUnreadNotifications,
  markNotificationsRead,
  UNREAD_NOTIFICATIONS_QUERY_KEY,
  type BrokerNotification,
} from '../lib/broker-notifications-api.js';
import { notificationTarget } from '../lib/broker-notification-target.js';
import { V4Group, V4Row } from './ux-v4.js';

/**
 * Dnešek → „Novinky" (krok NOTIF1 bod 3).
 *
 * Co se stalo v kanceláři a makléř to ještě nečetl: nová poptávka,
 * dokončený podpis, vyplněný klientský formulář, hotové prověření,
 * zaplacená faktura, blížící se lhůta. Prázdný blok se NEKRESLÍ — Dnešek
 * je „co teď", ne katalog funkcí (stejné pravidlo jako bloky CRM4).
 *
 * VĚTU SKLÁDÁ UI z druhu + popisku předmětu (`crm.notification.kind.*`
 * v katalogu `@reos/i18n`), ne server — jinak by čeština uvízla v databázi
 * a `en` by neměla paritu (CLAUDE.md §1.5).
 *
 * KLIK = PŘEČTENO: odkaz vede na místo a zároveň upozornění označí
 * přečtené (tím se sníží i počítadlo v menu, které čte týž dotaz).
 */

export const NOVINKY_ON_DNESEK = 6;

export function Novinky(): React.JSX.Element | null {
  const t = useT();
  const queryClient = useQueryClient();
  const unread = useQuery({
    queryKey: UNREAD_NOTIFICATIONS_QUERY_KEY,
    queryFn: () => listUnreadNotifications(NOVINKY_ON_DNESEK),
  });
  const markRead = useMutation({
    mutationFn: (ids: readonly string[]) => markNotificationsRead(ids),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: UNREAD_NOTIFICATIONS_QUERY_KEY });
    },
  });

  const items = unread.data?.items ?? [];
  if (items.length === 0) return null;

  const label = (item: BrokerNotification): string =>
    t(`crm.notification.kind.${item.kind}`, {
      subject: item.subjectLabel === '' ? t('webAdmin.novinky.subjectMissing') : item.subjectLabel,
    });

  return (
    <>
      <h2 className="rh-v4-h2" id="dnesek-novinky-h">
        {t('webAdmin.novinky.title')}
      </h2>
      <V4Group testId="dnesek-novinky" label={t('webAdmin.novinky.title')}>
        {items.map((item) => {
          const target = notificationTarget(item);
          return (
            <V4Row
              key={item.id}
              icon={
                <span className="rh-v4-ico" aria-hidden="true">
                  <BellRing size={16} strokeWidth={2} />
                </span>
              }
              title={label(item)}
              chevron
              render={(className, content) => (
                <Link
                  // Cesty jsou z `notificationTarget` (jedno misto pravdy);
                  // typovany router je nezna jako literal, proto `as never`.
                  to={target.to as never}
                  {...(target.params === undefined ? {} : { params: target.params as never })}
                  className={className}
                  data-testid={`dnesek-novinka-${item.id}`}
                  onClick={() => {
                    markRead.mutate([item.id]);
                  }}
                >
                  {content}
                </Link>
              )}
            />
          );
        })}
      </V4Group>
      <p className="rh-a2-card__foot">
        <button
          type="button"
          className="rh-a2-link"
          data-testid="dnesek-novinky-mark-all"
          onClick={() => {
            markRead.mutate([]);
          }}
        >
          {t('webAdmin.novinky.markAllRead')}
        </button>
      </p>
    </>
  );
}
